import { useRouter } from "next/router"
import { Routes } from "@blitzjs/next"
import React, { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Button,
} from "@mui/material"
import CopyDialogForm from "./CopyDialogForm"

interface EmailTemplateCopyData {
  title: string
}

async function copyEmailTemplate(emailTemplateCopyData, copyEmailTemplateMutation) {
  try {
    const emailTemplateCopy = await copyEmailTemplateMutation({
      id: emailTemplateCopyData.id,
      title: emailTemplateCopyData.title,
    })
    return [emailTemplateCopy, null]
  } catch (err) {
    return [null, err.toString()]
  }
}

export default function CopyDialog({
  emailTemplate,
  open,
  setOpen,
  setSnackbarData,
  copyEmailTemplateMutation,
}) {
  const router = useRouter()
  const [emailTemplateCopyData, setEmailTemplateCopyData] = useState<
    Partial<EmailTemplateCopyData>
  >({})
  const initialValues = useMemo<Partial<EmailTemplateCopyData>>(
    () => ({
      id: emailTemplate.id,
      title: emailTemplate.title + " (copy)",
    }),
    [emailTemplate]
  )

  const isCopyDisabled =
    !emailTemplateCopyData.title?.trim().length ||
    emailTemplateCopyData.title === emailTemplate.title // must change title

  return (
    <>
      <Dialog fullWidth maxWidth="md" open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Email template copy!</DialogTitle>
        <DialogContent>
          <DialogContentText>Provide a new unique title for this email template</DialogContentText>
          <Box sx={{ mt: 2 }}>
            <CopyDialogForm
              initialValues={initialValues}
              onSubmit={() => {}}
              onChange={setEmailTemplateCopyData}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            disabled={isCopyDisabled}
            onClick={async () => {
              const [emailTemplateCopy, errorMessage] = await copyEmailTemplate(
                emailTemplateCopyData,
                copyEmailTemplateMutation
              )
              if (errorMessage) {
                setSnackbarData({
                  type: "error",
                  text: `Failed to copy email template: ${errorMessage}`,
                })
                return
              }

              setSnackbarData({ type: "success", text: "Email template copied!", show: true })

              setOpen(false)
              await router.push(
                Routes.EditEmailTemplatePage({ emailTemplateId: emailTemplateCopy!.id })
              )
            }}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
